import { useMemo } from 'react'
import { type Address } from 'viem'
import { useReadContract } from 'wagmi'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { TransactionOverviewDetailsItem } from '@/features/dialogs/common/components/transaction-overview/TransactionOverviewDetailsItem'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { cn } from '@/ui/utils/style'
import { computeCapInfo, formatCompactNumber } from '@/ui/molecules/cap-detail/capDetailUtils'

interface SupplyCapDetailProps {
  vaultAddress: Address
  decimals: number
  symbol: string
}

export function SupplyCapDetail({ vaultAddress, decimals, symbol }: SupplyCapDetailProps) {
  const { data: capsData } = useReadContract({
    address: vaultAddress,
    abi: eVaultImplementationAbi,
    functionName: 'caps',
    chainId: E_LEND_CHAIN_ID,
    query: { enabled: !!vaultAddress },
  })

  const { data: totalAssetsRaw } = useReadContract({
    address: vaultAddress,
    abi: eVaultImplementationAbi,
    functionName: 'totalAssets',
    chainId: E_LEND_CHAIN_ID,
    query: { enabled: !!vaultAddress },
  })

  const info = useMemo(
    () => computeCapInfo(capsData?.[0], totalAssetsRaw, capsData !== undefined, decimals),
    [capsData, totalAssetsRaw, decimals],
  )

  if ('isLoading' in info || totalAssetsRaw === undefined) return null

  if (info.isUnlimited) {
    return (
      <TransactionOverviewDetailsItem label="Available to Supply">
        <span className="text-muted-foreground">Unlimited</span>
      </TransactionOverviewDetailsItem>
    )
  }

  const color = info.pct > 90 ? 'text-red-400' : info.pct > 80 ? 'text-yellow-400' : 'text-green-400'

  return (
    <TransactionOverviewDetailsItem label="Available to Supply">
      <div className="flex flex-col items-end gap-0.5">
        <span className="font-medium">
          {formatCompactNumber(info.remaining.toNumber())} {symbol}
        </span>
        <span className={cn('text-xs', color)}>{info.pct.toFixed(2)}% utilized</span>
      </div>
    </TransactionOverviewDetailsItem>
  )
}
